import {useEffect, useState} from "react";
import {FiZoomIn} from "react-icons/fi";

import {Icon} from "@/components/icon/Icon";
import {CheckDetailsAlert, CheckStatsLocation, LocationResponseTimes} from "@/lib/queries/checks.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {PerformanceDataType, PerformanceDataTypeLabel} from "@/lib/types/checks.ts";
import {ResponseTimeAreaChart} from "@/pages/checks/analysis/overview/ResponseTimeAreaChart.tsx";
import {ResponseTimeStatsCards} from "@/pages/checks/analysis/overview/ResponseTimeStatsCards.tsx";
import {formatResponseTime, getMinMaxAvg} from "@/pages/checks/analysis/overview/utils.ts";

export interface CheckResponseTimeOverviewProps {
  responseTimes: LocationResponseTimes[];
  locations: CheckStatsLocation[];
  intervalAlerts: CheckDetailsAlert[];
  start: Date;
  end: Date;
  fixedRange?: StandardDateRange;
  dataType: PerformanceDataType;
  onRangeChange: (start: Date | null, end: Date | null, fixedRange: StandardDateRange | null) => void;
  showTitle?: boolean;
  showStatsInSubtitle?: boolean;
  showMarkers?: boolean;
  showPlotbandsInLegend?: boolean;
}

export const CheckResponseTimeOverview = ({
  start,
  end,
  dataType,
  intervalAlerts,
  responseTimes,
  locations,
  onRangeChange,
  showTitle = true,
  showStatsInSubtitle = false,
  showMarkers,
  showPlotbandsInLegend,
}: CheckResponseTimeOverviewProps) => {
  const [selectedLocations, setSelectedLocations] = useState<string[]>(responseTimes.map((l) => l.id));

  const stats = getMinMaxAvg(responseTimes.filter((t) => selectedLocations.includes(t.id)));
  const title = PerformanceDataTypeLabel[dataType];
  const [subtitle, setSubtitle] = useState(
    `High ${formatResponseTime(stats.max)},
      Low ${formatResponseTime(stats.min)},
      Avg ${formatResponseTime(stats.avg)}`
  );

  useEffect(() => {
    const stats = getMinMaxAvg(responseTimes.filter((t) => selectedLocations.includes(t.id)));
    const newValue = `High ${stats.max ? formatResponseTime(stats.max) : "N/A"},
      Low ${stats.min ? formatResponseTime(stats.min) : "N/A"},
      Avg ${stats.avg ? formatResponseTime(stats.avg) : "N/A"}`;
    setSubtitle(newValue);
  }, [responseTimes, setSubtitle, selectedLocations]);

  return (
    <div className={"d-flex flex-column gap-4"}>
      <div className={"d-flex justify-content-between"}>
        {showTitle && <div className={"pulseTextH2Bold"}>{title}</div>}
        <div className={"d-flex align-items-center gap-2 pulseTextBodySmall text-end pulse-color-gray-600"}>
          <Icon icon={FiZoomIn} />
          Drag chart to zoom
        </div>
      </div>
      {showStatsInSubtitle && <div className="pulseTextBodySmall text-end">{subtitle}</div>}
      <ResponseTimeAreaChart
        start={start}
        end={end}
        locations={locations}
        responseTimes={responseTimes}
        onLocationClick={(id, visible) => {
          if (visible) {
            setSelectedLocations([...selectedLocations, id]);
          } else {
            setSelectedLocations(selectedLocations.filter((l) => l != id));
          }
        }}
        onRangeChange={onRangeChange}
        alerts={intervalAlerts}
        showMarkers={showMarkers}
        showPlotbandsInLegend={showPlotbandsInLegend}
      />
      {!showStatsInSubtitle && <ResponseTimeStatsCards stats={stats} />}
    </div>
  );
};
